import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { LayoutItem } from 'react-grid-layout';
import {
  useDashboards,
  useCreateDashboard,
  useUpdateDashboard,
  useDeleteDashboard,
  useAddWidget,
  useDeleteWidget,
} from '../../../api/hooks/useDashboards';
import { DashboardPicker } from './DashboardPicker';
import { DashboardGrid } from './DashboardGrid';
import { WidgetBuilderModal } from './WidgetBuilderModal';
import { ShareDialog } from './ShareDialog';
import { DashboardFormModal } from './DashboardFormModal';
import { DashboardDeleteModal } from './DashboardDeleteModal';
import { DEFAULT_SPEC, type WidgetSpec } from '../../../types/analytics';
import { useAuthStore } from '@store/authStore';
import Button from '@components/common/Button';
import { EmptyState } from '@components/common/EmptyState';
import { useDashboardBuilderStore } from '../../../store/dashboardBuilderStore';
import { useCatalog } from '../../../api/hooks/useAnalytics';
import { usePlanFeature } from '../../../api/hooks/useSubscription';
import { useChatStore } from '@store/chatStore';
import { buildDashboardPrimer, DASHBOARD_STARTERS } from './dashboardChatPrimer';

export function CustomDashboards() {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canRead = hasPermission('dashboard.read');
  const canCreate = hasPermission('dashboard.create');
  const canShare = hasPermission('dashboard.share');
  const dashboardsFeatureEnabled = usePlanFeature('dashboards');
  const canUseDashboards = dashboardsFeatureEnabled && canRead;

  const { data: dashboards } = useDashboards(canUseDashboards);
  const createDashboard = useCreateDashboard();
  const updateDashboard = useUpdateDashboard();
  const deleteDashboard = useDeleteDashboard();
  const addWidget = useAddWidget();
  const deleteWidget = useDeleteWidget();

  const [activeId, setActiveId] = useState<string | null>(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [prefill, setPrefill] = useState<{ title: string; spec: WidgetSpec } | null>(null);
  const [formMode, setFormMode] = useState<'create' | 'rename' | null>(null);

  const active = useMemo(
    () => dashboards?.find((d) => d.id === (activeId ?? dashboards?.[0]?.id)) ?? null,
    [dashboards, activeId],
  );
  const activeWidgetSignature = active?.widgets.map((widget) => widget.id).join(',') ?? '';
  const editable = active ? active.access_level === 'owner' || active.access_level === 'edit' : false;
  const canDelete = active?.access_level === 'owner';

  const pending = useDashboardBuilderStore((s) => s.pending);
  const clearPending = useDashboardBuilderStore((s) => s.clear);

  useEffect(() => {
    if (pending) {
      // NOTE: if a draft arrives while there is no `active` dashboard, the
      // builder modal below won't mount (it requires `builderOpen && active`).
      // Auto-creating a dashboard in that case is out of scope for this task.
      setPrefill(pending);
      setBuilderOpen(true);
      clearPending();
    }
  }, [pending, clearPending]);

  const pendingDashboardId = useDashboardBuilderStore((s) => s.pendingDashboardId);
  const clearDashboard = useDashboardBuilderStore((s) => s.clearDashboard);
  const qc = useQueryClient();

  useEffect(() => {
    if (pendingDashboardId) {
      qc.invalidateQueries({ queryKey: ['dashboards'] });
      setActiveId(pendingDashboardId);
      clearDashboard();
    }
  }, [pendingDashboardId, clearDashboard, qc]);

  const { data: catalog } = useCatalog(canUseDashboards && canCreate);
  const expandChat = useChatStore((s) => s.expand);
  const addMessage = useChatStore((s) => s.addMessage);
  const sendPrompt = useDashboardBuilderStore((s) => s.sendPrompt);

  const openCreationChat = () => {
    expandChat();
    addMessage(buildDashboardPrimer(catalog));
  };

  const openDeleteModal = () => {
    deleteDashboard.reset();
    setDeleteOpen(true);
  };

  const closeDeleteModal = () => {
    if (deleteDashboard.isPending) return;
    setDeleteOpen(false);
    deleteDashboard.reset();
  };

  const handleDeleteDashboard = () => {
    if (!active) return;
    const dashboardId = active.id;
    const nextDashboardId = dashboards?.find((dashboard) => dashboard.id !== dashboardId)?.id ?? null;

    deleteDashboard.mutate(dashboardId, {
      onSuccess: () => {
        setDeleteOpen(false);
        setActiveId(nextDashboardId);
      },
    });
  };

  useEffect(() => {
    if (!canCreate) return;
    // Wait for the catalog so the primer lists real metrics (not the loading placeholder).
    if (dashboards && dashboards.length === 0 && catalog) {
      if (sessionStorage.getItem('dashboard.primerShown')) return;
      sessionStorage.setItem('dashboard.primerShown', '1');
      openCreationChat();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboards, canCreate, catalog]);

  if (!canUseDashboards) return null;

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-content-primary">Dashboards</h2>
        <div className="flex items-center gap-2">
          {dashboards && dashboards.length > 0 && (
            <DashboardPicker dashboards={dashboards} activeId={active?.id ?? null} canCreate={canCreate}
              onSelect={setActiveId} onCreate={() => setFormMode('create')} />
          )}
          {editable && active && (
            <Button size="sm" variant="secondary" onClick={() => setFormMode('rename')}>Rename</Button>
          )}
          {editable && <Button size="sm" variant="secondary" onClick={() => setBuilderOpen(true)}>Widget</Button>}
          {canShare && active && <Button size="sm" variant="secondary" onClick={() => setShareOpen(true)}>Share</Button>}
          {canDelete && active && (
            <Button size="sm" variant="danger" onClick={openDeleteModal}>Delete</Button>
          )}
          {canCreate && (
            <Button size="sm" variant="secondary" onClick={openCreationChat}>
              Create with AI
            </Button>
          )}
        </div>
      </div>

      {(!dashboards || dashboards.length === 0) && (
        <EmptyState
          title={canCreate ? 'No dashboards yet' : 'No dashboards shared with you yet'}
          actions={canCreate ? (
            <>
              <Button size="sm" onClick={() => setFormMode('create')}>Create dashboard</Button>
              <Button size="sm" variant="secondary" onClick={openCreationChat}>Create with AI</Button>
            </>
          ) : undefined}
        >
          {canCreate ? 'Create one to start tracking AP metrics.' : undefined}
          {canCreate && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {DASHBOARD_STARTERS.map((s) => (
                <Button key={s} size="xs" variant="ghost" onClick={() => sendPrompt(s)}>{s}</Button>
              ))}
            </div>
          )}
        </EmptyState>
      )}

      {active && (
        <DashboardGrid key={`${active.id}:${activeWidgetSignature}`} dashboard={active} editable={editable}
          onLayoutChange={(layout: LayoutItem[]) => updateDashboard.mutate({ id: active.id, patch: { layout } })}
          onRemoveWidget={(widgetId) => deleteWidget.mutate({ dashboardId: active.id, widgetId })} />
      )}

      {builderOpen && active && (
        <WidgetBuilderModal open
          initialSpec={prefill?.spec ?? DEFAULT_SPEC('invoice_spend')}
          initialTitle={prefill?.title ?? ''}
          onClose={() => { setBuilderOpen(false); setPrefill(null); }}
          onSave={(title: string, spec: WidgetSpec) => {
            addWidget.mutate({ dashboardId: active.id, title, spec });
            setBuilderOpen(false); setPrefill(null);
          }} />
      )}

      {shareOpen && active && <ShareDialog dashboardId={active.id} onClose={() => setShareOpen(false)} />}

      {active && (
        <DashboardDeleteModal
          dashboardName={active.name}
          isOpen={deleteOpen}
          isLoading={deleteDashboard.isPending}
          errorMessage={deleteDashboard.isError ? 'Unable to delete this dashboard. Please try again.' : undefined}
          onCancel={closeDeleteModal}
          onConfirm={handleDeleteDashboard} />
      )}

      {formMode && (
        <DashboardFormModal
          key={`${formMode}-${active?.id ?? 'new'}`}
          open mode={formMode}
          initialName={formMode === 'rename' ? active?.name ?? '' : ''}
          initialDescription={formMode === 'rename' ? active?.description ?? '' : ''}
          onClose={() => setFormMode(null)}
          onSubmit={(name, description) => {
            if (formMode === 'create') {
              createDashboard.mutate({ name, description }, { onSuccess: (d) => setActiveId(d.id) });
            } else if (active) {
              updateDashboard.mutate({ id: active.id, patch: { name, description } });
            }
            setFormMode(null);
          }} />
      )}
    </section>
  );
}
